import React, { useMemo } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faFile, 
  faCheckDouble, 
  faArrowUpRightFromSquare, 
  faPhone, 
  faReply, 
  faCircleCheck,
  faImage,
  faVideo,
  faMapMarkerAlt
} from '@fortawesome/free-solid-svg-icons';
import './managecampaigns.css';

// Helper to format WhatsApp markdown (*bold*, _italic_, ~strike~, `code`) safely
function formatWhatsAppMarkdown(text) {
  if (!text || typeof text !== 'string') return '';

  let formatted = text;
  
  // Escape HTML characters to prevent XSS in replacement
  formatted = formatted
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;');

  // Bold: *text*
  formatted = formatted.replace(/\*([^*]+)\*/g, '<strong>$1</strong>');
  
  // Italic: _text_
  formatted = formatted.replace(/_([^_]+)_/g, '<em>$1</em>');
  
  // Strikethrough: ~text~
  formatted = formatted.replace(/~([^~]+)~/g, '<del>$1</del>');
  
  // Monospace: `text`
  formatted = formatted.replace(/`([^`]+)`/g, '<code style="background: #f0f2f5; padding: 2px 4px; border-radius: 4px; font-family: monospace;">$1</code>');

  return formatted;
}

export default function TemplatePreview({
  templateVariables = {},
  selectedTemplate = null,
  buttonPreview = null,
  previewMessage = null,
  fotterPriviewFunction = null,
  sampleData = null // Optional sample contact or CSV row data for dynamic replacement
}) {
  // Safe extraction of template components array
  const components = useMemo(() => {
    if (!selectedTemplate) return [];
    if (Array.isArray(selectedTemplate.components)) {
      return selectedTemplate.components;
    }
    if (typeof selectedTemplate.components === 'string') {
      try {
        return JSON.parse(selectedTemplate.components);
      } catch (e) {
        console.error('Failed to parse template components JSON:', e);
        return [];
      }
    }
    return [];
  }, [selectedTemplate]);

  // Extract individual component objects
  const headerComponent = useMemo(() => components.find(c => c.type === 'HEADER'), [components]);
  const bodyComponent = useMemo(() => components.find(c => c.type === 'BODY'), [components]);
  const footerComponent = useMemo(() => components.find(c => c.type === 'FOOTER'), [components]);
  const buttonComponent = useMemo(() => components.find(c => c.type === 'BUTTONS'), [components]);

  // Current timestamp string (e.g., 10:45 AM)
  const currentTime = useMemo(() => {
    const now = new Date();
    return now.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  }, []);

  // Helper to resolve variable value (handling STATIC vs DYNAMIC mapping)
  const getVariableDisplayValue = (varKey, defaultLabel) => {
    const varObj = templateVariables[varKey];
    if (!varObj) return defaultLabel;

    if (typeof varObj === 'string') {
      return varObj.trim() !== '' ? varObj : defaultLabel;
    }

    const { value_type, value } = varObj;

    if (value_type === 'STATIC') {
      return value && value.trim() !== '' ? value : defaultLabel;
    }

    if (value_type === 'DYNAMIC') {
      if (!value) return defaultLabel;
      // If sample data exists (e.g. from contact list or CSV sample row), replace key with actual value
      if (sampleData && sampleData[value] !== undefined && sampleData[value] !== null && sampleData[value] !== '') {
        return sampleData[value];
      }
      // Human-readable field fallback label
      const labelMap = {
        name: 'John Doe',
        mobile: '+91 9876543210',
        email: 'john@example.com',
        address: '123 Main St',
        city: 'Mumbai',
        district: 'Mumbai Suburban',
        state: 'Maharashtra',
        pin: '400001',
        parameter1: 'Sample Param 1',
        parameter2: 'Sample Param 2',
        parameter3: 'Sample Param 3',
        parameter4: 'Sample Param 4',
        parameter5: 'Sample Param 5'
      };
      return labelMap[value] || `[${value.charAt(0).toUpperCase() + value.slice(1)}]`;
    }

    return defaultLabel;
  };

  // Render Header Component
  const renderHeader = () => {
    if (!headerComponent) {
      // Check legacy prop variable if component object is missing
      if (templateVariables.header && templateVariables.header.value) {
        return <div className="wa-body-text fw-bold mb-2">{templateVariables.header.value}</div>;
      }
      return null;
    }

    const format = headerComponent.format || 'TEXT';

    if (format === 'TEXT') {
      let text = headerComponent.text || '';
      // Replace header {{1}} variable
      const headerVal = getVariableDisplayValue('header', '[Header Value]');
      text = text.replace(/\{\{1\}\}/g, headerVal);

      return (
        <div 
          className="wa-body-text fw-bold mb-2"
          dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(text) }}
        />
      );
    }

    if (format === 'IMAGE') {
      const mediaUrl = templateVariables.header?.value || headerComponent.example?.header_handle?.[0];
      return (
        <div className="wa-header-media-preview">
          {mediaUrl ? (
            <img src={mediaUrl} alt="Header Preview" onError={(e) => { e.target.style.display = 'none'; }} />
          ) : (
            <div className="p-4 text-muted bg-light rounded d-flex flex-column align-items-center justify-content-center">
              <FontAwesomeIcon icon={faImage} size="2x" className="mb-2 text-secondary" />
              <small>Image Header Preview</small>
            </div>
          )}
        </div>
      );
    }

    if (format === 'VIDEO') {
      const videoUrl = templateVariables.header?.value || headerComponent.example?.header_handle?.[0];
      return (
        <div className="wa-header-media-preview">
          {videoUrl ? (
            <video src={videoUrl} controls style={{ width: '100%', maxHeight: '180px' }} />
          ) : (
            <div className="p-4 text-muted bg-light rounded d-flex flex-column align-items-center justify-content-center">
              <FontAwesomeIcon icon={faVideo} size="2x" className="mb-2 text-secondary" />
              <small>Video Header Preview</small>
            </div>
          )}
        </div>
      );
    }

    if (format === 'DOCUMENT') {
      return (
        <div className="wa-document-box">
          <FontAwesomeIcon icon={faFile} className="wa-document-icon" />
          <div className="wa-document-info">
            <span className="wa-document-name">Document Attachment</span>
            <span className="wa-document-subtext">PDF / DOCX File</span>
          </div>
        </div>
      );
    }

    if (format === 'LOCATION') {
      return (
        <div className="wa-document-box">
          <FontAwesomeIcon icon={faMapMarkerAlt} className="wa-document-icon text-danger" />
          <div className="wa-document-info">
            <span className="wa-document-name">Location Pin</span>
            <span className="wa-document-subtext">Shared Location Data</span>
          </div>
        </div>
      );
    }

    return null;
  };

  // Render Body Component
  const renderBody = () => {
    // If external function prop is supplied and returns content, use it as fallback
    if (typeof previewMessage === 'function') {
      const customMsg = previewMessage();
      if (customMsg && typeof customMsg === 'string' && customMsg.trim() !== '') {
        return (
          <div 
            className="wa-body-text"
            dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(customMsg) }}
          />
        );
      }
    }

    if (!bodyComponent || !bodyComponent.text) {
      return <div className="wa-body-text text-muted italic">No body text available</div>;
    }

    let text = bodyComponent.text;

    // Replace all body variables {{1}}, {{2}}, etc.
    text = text.replace(/\{\{(\d+)\}\}/g, (match, num) => {
      const key = `body_${num}`;
      const altKey = `body${num}`;
      const fallbackLabel = `[Variable ${num}]`;
      
      if (templateVariables[key]) {
        return getVariableDisplayValue(key, fallbackLabel);
      }
      if (templateVariables[altKey]) {
        return getVariableDisplayValue(altKey, fallbackLabel);
      }
      return fallbackLabel;
    });

    return (
      <div 
        className="wa-body-text"
        dangerouslySetInnerHTML={{ __html: formatWhatsAppMarkdown(text) }}
      />
    );
  };

  // Render Footer Component
  const renderFooter = () => {
    if (typeof fotterPriviewFunction === 'function') {
      const customFooter = fotterPriviewFunction();
      if (customFooter) {
        return <div className="wa-footer-text">{customFooter}</div>;
      }
    }

    if (!footerComponent || !footerComponent.text) return null;

    return <div className="wa-footer-text">{footerComponent.text}</div>;
  };

  // Render Buttons Component
  const renderButtons = () => {
    if (typeof buttonPreview === 'function') {
      const customBtns = buttonPreview();
      if (customBtns) return customBtns;
    }

    if (!buttonComponent || !Array.isArray(buttonComponent.buttons) || buttonComponent.buttons.length === 0) {
      return null;
    }

    return (
      <div className="wa-buttons-container">
        {buttonComponent.buttons.map((btn, idx) => (
          <div key={idx} className="wa-btn-item">
            {btn.type === 'URL' && (
              <>
                <FontAwesomeIcon icon={faArrowUpRightFromSquare} size="xs" />
                <span>{btn.text || 'Visit Website'}</span>
              </>
            )}
            {btn.type === 'PHONE_NUMBER' && (
              <>
                <FontAwesomeIcon icon={faPhone} size="xs" />
                <span>{btn.text || 'Call Us'}</span>
              </>
            )}
            {btn.type === 'QUICK_REPLY' && (
              <>
                <FontAwesomeIcon icon={faReply} size="xs" />
                <span>{btn.text || 'Reply'}</span>
              </>
            )}
            {btn.type !== 'URL' && btn.type !== 'PHONE_NUMBER' && btn.type !== 'QUICK_REPLY' && (
              <span>{btn.text || 'Action Button'}</span>
            )}
          </div>
        ))}
      </div>
    );
  };

  return (
    <div className="wa-preview-card w-100">
      {/* WhatsApp Business Header */}
      <div className="wa-preview-header">
        <div className="wa-avatar-icon">WA</div>
        <div className="wa-header-info">
          <div className="d-flex align-items-center gap-1">
            <span className="wa-header-name">WhatsApp Business</span>
            <FontAwesomeIcon icon={faCircleCheck} className="text-white small ms-1" style={{ fontSize: '11px' }} />
          </div>
          <span className="wa-header-status">Official Business Account</span>
        </div>
      </div>

      {/* WhatsApp Chat Background */}
      <div className="wa-chat-container">
        <div className="wa-bubble">
          {/* Header Preview */}
          {renderHeader()}

          {/* Body Preview */}
          {renderBody()}

          {/* Footer Preview */}
          {renderFooter()}

          {/* Timestamp & Read Ticks */}
          <div className="wa-time-stamp">
            <span>{currentTime}</span>
            <FontAwesomeIcon icon={faCheckDouble} className="wa-ticks" />
          </div>

          {/* Buttons Preview */}
          {renderButtons()}
        </div>
      </div>
    </div>
  );
}
